export {
  pageWrap,
  pageTitle,
  pageSubtitle,
  pageHint,
  toolbarCard,
  actionsRow,
  btnSecondary,
  btnPrimary,
  tablePanel,
  tabStrip,
  tabBtnStyle,
  tableHeaderBar,
  th,
  td,
  tableFooter,
  infoBanner,
  emptyState,
} from '../basecamp/basecampPageLayout'

export {
  glassPanelStyle,
  glassPageBackground,
  glassInputStyle,
} from './agent/agentChatLayout'

/** Frosted toolbar for Knowledge search page. */
export const glassToolbarCard = {
  marginBottom: 20,
  padding: '20px 20px 16px',
  maxWidth: 1200,
  borderRadius: 16,
  background: 'linear-gradient(145deg, rgba(255,255,255,0.55) 0%, rgba(245,240,235,0.35) 100%)',
  backdropFilter: 'blur(18px) saturate(160%)',
  WebkitBackdropFilter: 'blur(18px) saturate(160%)',
  border: '1px solid rgba(255,255,255,0.65)',
  boxShadow: '0 4px 24px rgba(28, 25, 23, 0.06)',
}

export const glassDividerLine = {
  flex: 1,
  height: 1,
  background: 'linear-gradient(90deg, transparent, rgba(193,127,89,0.35), transparent)',
}

export const glassReportPanel = {
  marginTop: 16,
  padding: '20px 22px',
  borderRadius: 16,
  background: 'linear-gradient(145deg, rgba(255,255,255,0.55) 0%, rgba(245,240,235,0.35) 100%)',
  backdropFilter: 'blur(18px) saturate(160%)',
  WebkitBackdropFilter: 'blur(18px) saturate(160%)',
  border: '1px solid rgba(255,255,255,0.65)',
  boxShadow: '0 4px 24px rgba(28, 25, 23, 0.06)',
}

export const laneBadge = (lane) => {
  const colors = {
    document: { bg: '#dbeafe', color: '#1d4ed8' },
    structured: { bg: '#f3e8ff', color: '#7c3aed' },
    external: { bg: '#fef3c7', color: '#b45309' },
    reference: { bg: '#f1f5f9', color: '#475569' },
  }
  return colors[lane] ?? colors.reference
}

/** Human-facing sync status labels (DB may still store `synced`). */
export const formatSyncStatus = (status) => {
  const labels = {
    queued: 'Queued',
    parsing: 'Parsing',
    embedding: 'Embedding',
    synced: 'Indexed',
    parse_failed: 'Parse failed',
    embed_failed: 'Embed failed',
    pending_update: 'Pending update',
    source_deleted: 'Source deleted',
  }
  return labels[status] ?? status ?? '—'
}

export const statusBadge = (status) => {
  const colors = {
    queued: { bg: '#f1f5f9', color: '#475569' },
    parsing: { bg: '#fef3c7', color: '#b45309' },
    embedding: { bg: '#fef3c7', color: '#b45309' },
    synced: { bg: '#dcfce7', color: '#15803d' },
    parse_failed: { bg: '#fee2e2', color: '#b91c1c' },
    embed_failed: { bg: '#fee2e2', color: '#b91c1c' },
    pending_update: { bg: '#fef3c7', color: '#b45309' },
    source_deleted: { bg: '#f1f5f9', color: '#94a3b8' },
  }
  return colors[status] ?? colors.queued
}

/** True when status is actively processing (UI may pulse). */
export const isActiveSyncStatus = (status) => status === 'parsing' || status === 'embedding'

/** Adapters that ingest into the vector index (Phase 1 + Phase 2 federation + structured). */
export const LIVE_ADAPTERS = new Set([
  'manual_upload',
  'static_docs',
  'basecamp_recordings',
  'outlook_digest',
  'idea_board_attachments',
  'clients_structured',
  'bpl_structured',
  'adimpact_alerts_structured',
  'race_tracker_structured',
  'basecamp_projects_structured',
  'sharepoint_files',
])

/** Explicit reasons for sources that intentionally have no Sync adapter. */
const SOURCE_SKIP_REASONS = {
  analytics:
    'Skipped — computed from Client Dashboard. Index clients instead; analytics are live rollups, not stored rows.',
  elections:
    'Skipped — static UI data (no database). Election tables live in frontend JS modules; not OLTP rows for watermark sync.',
  'primary-calendar':
    'Skipped — static primary dates file. Client overlay already covered by Client Dashboard index.',
  resources:
    'Skipped — document catalog (not structured). Needs a document/link adapter later, not row watermark sync.',
  'ad-ops-hq': 'Skipped — external site only. No local data to index (opens Ad Ops HQ URL).',
}

/**
 * Why Sync is unavailable for this source. Returns null when an adapter exists
 * or the source is simply not yet implemented without a known skip reason.
 */
export function sourceSkipReason(source) {
  if (!source) return null
  if (source.adapter_key) return null
  return SOURCE_SKIP_REASONS[source.source_key] ?? null
}

/** Sources that are placeholders (external URL, static UI, etc.) — hide from KB tables. */
export function isSkippedKbSource(source) {
  return Boolean(sourceSkipReason(source))
}

export function sourcePhaseHint(adapterKey, source) {
  if (!adapterKey) {
    const skip = sourceSkipReason(source)
    if (skip) return skip
    return 'No adapter'
  }
  if (adapterKey === 'manual_upload') return 'Live — upload to index'
  if (adapterKey === 'static_docs') return 'Live — Sync User Guide'
  if (adapterKey === 'basecamp_recordings') return 'Live — Sync Basecamp (enable federation)'
  if (adapterKey === 'outlook_digest') return 'Live — Sync Outlook digests (enable federation)'
  if (adapterKey === 'idea_board_attachments') return 'Live — Sync Idea Board'
  if (adapterKey === 'clients_structured') return 'Live — Sync Client Dashboard (incremental)'
  if (adapterKey === 'bpl_structured') return 'Live — Sync Big Political List (stations)'
  if (adapterKey === 'adimpact_alerts_structured') return 'Live — Sync AdImpact Alerts (incremental)'
  if (adapterKey === 'race_tracker_structured') return 'Live — Sync Race Tracker (races)'
  if (adapterKey === 'basecamp_projects_structured') return 'Live — Sync Basecamp project mapping'
  if (adapterKey === 'sharepoint_files') return 'Live — Sync SharePoint libraries (enable federation)'
  if (LIVE_ADAPTERS.has(adapterKey)) return 'Live — searchable when Indexed'
  return 'Not indexed yet'
}

/** True when Sources UI should show a Sync button. */
export function isSyncableAdapter(adapterKey) {
  return (
    adapterKey === 'static_docs' ||
    adapterKey === 'basecamp_recordings' ||
    adapterKey === 'outlook_digest' ||
    adapterKey === 'idea_board_attachments' ||
    adapterKey === 'clients_structured' ||
    adapterKey === 'bpl_structured' ||
    adapterKey === 'adimpact_alerts_structured' ||
    adapterKey === 'race_tracker_structured' ||
    adapterKey === 'basecamp_projects_structured' ||
    adapterKey === 'sharepoint_files'
  )
}

/** Shallow-stable compare for avoiding React re-renders on poll. */
export function stableJsonEqual(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}
